import { modulesBySlug } from "./content.js";

// Routing for the public pages — the ones a visitor can reach before they sign
// in. App.tsx owns the history state; this module owns the URL vocabulary, the
// same split the signed-in app uses (src/app/routing.ts).

export type MarketingRoute =
  | { kind: "landing" }
  | { kind: "modules" }
  | { kind: "module"; slug: string }
  | { kind: "contact" }
  // Not produced by marketingRoute(): a URL that matches nothing here may still
  // be a page of the app, and only App.tsx knows the app's own vocabulary. It
  // builds this route for a signed-out visitor whose URL is neither. See the
  // call site for why the app's deep links must NOT reach it.
  | { kind: "notFound" };

export const modulesPath = "/product";
export const contactPath = "/contact";
export const signInPath = "/signin";
export const landingPath = "/";

/**
 * The landing page's second spelling, public for a signed-in visitor too (#856).
 *
 * "/" cannot carry that job: it is the Dashboard for anyone with a session, on
 * purpose (see marketingRoute), so a link to "/" from inside the app is a link
 * to the Dashboard. The app's way back to the landing page therefore needs an
 * address that does not change meaning once you are signed in, the same way
 * /product does not. Nothing on the public site points here — its own links
 * stay on "/", which is still the canonical spelling marketingRoutePath emits.
 */
export const welcomePath = "/welcome";

export function modulePath(slug: string): string {
  return `${modulesPath}/${encodeURIComponent(slug)}`;
}

function normalize(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * The public page for a URL, or null when the URL belongs to the app itself.
 *
 * "/" is the landing page ONLY for a visitor who is not signed in: for everyone
 * else it is the Dashboard, and a bookmark of the app must not turn into a
 * product page after sign-in. The /product pages stay public either way, so a
 * link to one keeps working when it is opened by a person who is signed in, and
 * /welcome gives the landing page itself an address with that same property —
 * the one the app's own link back to it uses.
 */
export function marketingRoute(pathname: string, signedIn: boolean): MarketingRoute | null {
  const path = normalize(pathname);
  if (path === landingPath) return signedIn ? null : { kind: "landing" };
  // The same page at an address that stays public either way; see welcomePath.
  if (path === welcomePath) return { kind: "landing" };
  if (path === modulesPath) return { kind: "modules" };
  // Public either way: a signed-in visitor with a question is still a visitor.
  if (path === contactPath) return { kind: "contact" };
  if (!path.startsWith(`${modulesPath}/`)) return null;

  const raw = path.slice(modulesPath.length + 1);
  if (!raw || raw.includes("/")) return { kind: "modules" };
  let slug = raw;
  try {
    slug = decodeURIComponent(raw);
  } catch {
    // A malformed escape is not a module name; fall back to the raw segment.
  }
  // An unknown slug shows the module index rather than an error page: the list
  // is what the visitor was looking for, and every module is one click away.
  return modulesBySlug.has(slug) ? { kind: "module", slug } : { kind: "modules" };
}

/**
 * Whether a URL shows a public surface. The inline boot script in
 * apps/web/index.html hand-rolls this same test to pick the first painted
 * theme, so the two must agree — keep them in step.
 *
 * /signin is one of these even though it is not a marketing route. It is the
 * door the public pages send a visitor through, and it used to follow the
 * visitor's own preference while the page linking to it was pinned light: a
 * dark-mode visitor clicking "Sign in" on a white page fell into a black one.
 */
export function isPublicPagePath(pathname: string, signedIn: boolean): boolean {
  return normalize(pathname) === signInPath || marketingRoute(pathname, signedIn) !== null;
}

/** The canonical URL for a public route, used by the in-page links. */
export function marketingRoutePath(route: MarketingRoute): string {
  if (route.kind === "landing") return landingPath;
  if (route.kind === "modules") return modulesPath;
  if (route.kind === "contact") return contactPath;
  // A not-found URL has no canonical form; the header only reads this to decide
  // which nav item is current, and on that page none of them is.
  if (route.kind === "notFound") return landingPath;
  return modulePath(route.slug);
}
